import type { Narrative } from "./types";

import { beatHasVerbatim } from "./types";

/**
 * Long Form AI adoption/usage telemetry (openspec `long-form-ai` §7).
 *
 * V0 answers three questions: code health (errors — handled by `sendErrorMetric` + logs, not here),
 * adoption/usage (these events), and sales impact (correlated by `sessionId` against the existing
 * enroll/decline funnel — no new events). Every event carries the same dims so the OpenSearch
 * dashboard can slice by session/expert/product/partner. `isFallback` was always `true` before the
 * listener was mounted, because no producer path existed; a producer-delivered narrative now sets it
 * `false` — see `receiveNarrativeEvent` / `applyPendingNarrative`.
 *
 * These are LFA-namespaced and additive — the panel keeps its existing generic `ButtonClicked_*`
 * events (legacy-funnel parity); these give the narrative its own, distinguishable funnel (e.g.
 * guide-send vs enroll-send, which both emit the generic `ButtonClicked_SendLink` today).
 */
export const LFA_EVENT = {
    narrativeShown: "LongFormAiNarrativeShown",
    newScriptLoaded: "LongFormAiNewScriptLoaded",
    beatCovered: "LongFormAiBeatCovered",
    beatSkipped: "LongFormAiBeatSkipped",
    guideSent: "LongFormAiGuideSent",
    enrollSent: "LongFormAiEnrollSent",
    quickRebuttalOpened: "LongFormAiQuickRebuttalOpened",
    declined: "LongFormAiDeclined",
} as const;

/** Dimensions attached to every LFA business event. */
export interface LfaDims {
    sessionId: string;
    employeeId: string;
    productSku: string;
    partner: string;
    /** `false` for a producer-delivered narrative, `true` for the static client fallback. */
    isFallback: boolean;
}

type DispatchBusinessEvent = (name: string, data?: Record<string, unknown>) => void | Promise<void>;

/**
 * Whether the beat with `beatId` is required — i.e. carries verbatim (locked) copy that must be read
 * exactly. Looked up by the beat's stable id (not index; the beat count can change — task 1.21).
 * Unknown ids are treated as not-required rather than throwing, so a stale id can't break a click.
 */
export const isBeatRequiredById = (narrative: Narrative, beatId: string): boolean => {
    for (const step of narrative.steps) {
        const beat = step.beats.find((b) => b.id === beatId);
        if (beat) return beatHasVerbatim(beat);
    }
    return false;
};

/**
 * Bind the LFA adoption/usage events to a session's dims. Returns one emitter per event; each merges
 * the dims with any event-specific fields (`beatId`, `required`). Dispatches are fire-and-forget.
 */
export const createLfaTelemetry = (dispatch: DispatchBusinessEvent, dims: LfaDims) => ({
    narrativeShown: () => void dispatch(LFA_EVENT.narrativeShown, { ...dims }),
    // Only the LOAD is an event. Arrivals are already one info log per inbound narrative in
    // `useLongFormAiListener`, so "arrived but never loaded" is arrivals minus loads — no second event,
    // and no store flag to dedupe a banner-shown event across remounts.
    //
    // `isFallback: false` is written here rather than taken from `dims`, and it is correct by
    // construction: only `receiveNarrativeEvent` ever stages a `pendingNarrative`, and it only stages
    // producer narratives. Reading it from `dims` would report the script being REPLACED — in V0 the
    // fallback — so the one event that marks the fallback → producer handover would be filed in the
    // fallback bucket, and `dims` is memoised in the panel so it cannot self-correct.
    newScriptLoaded: () => void dispatch(LFA_EVENT.newScriptLoaded, { ...dims, isFallback: false }),
    beatCovered: (beatId: string, required: boolean) =>
        void dispatch(LFA_EVENT.beatCovered, { ...dims, beatId, required }),
    beatSkipped: (beatId: string, required: boolean) =>
        void dispatch(LFA_EVENT.beatSkipped, { ...dims, beatId, required }),
    guideSent: () => void dispatch(LFA_EVENT.guideSent, { ...dims }),
    enrollSent: () => void dispatch(LFA_EVENT.enrollSent, { ...dims }),
    quickRebuttalOpened: () => void dispatch(LFA_EVENT.quickRebuttalOpened, { ...dims }),
    declined: () => void dispatch(LFA_EVENT.declined, { ...dims }),
});

export type LfaTelemetry = ReturnType<typeof createLfaTelemetry>;
